import React, { useState } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { useAssetNodeForMachine } from "../../../hooks/useAssetNodeForMachine";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_FIELD_ROLES } from "../../cmms/cmmsRoles";
import QuickReportDialog from "../../cmms/components/QuickReportDialog";

// Operator entry point on the machine page. Resolves the machine's linked asset node
// from the CMMS hierarchy and opens QuickReportDialog. Disabled (with a tooltip) when
// the machine is not linked to the maintenance system.
//
// When `compact` is true, renders an orange-triangle IconButton (intended to live
// inside the Machine-comments icon column). Otherwise renders the standalone
// contained Button.
export default function MachineReportIssueButton({ machineId, machineName, companyKey, compact = false }) {
  const [reportOpen, setReportOpen] = useState(false);

  const { assetNode } = useAssetNodeForMachine(machineId, companyKey);

  const linked = Boolean(assetNode?.id);
  const tooltipText = linked ? "Report problem" : "Machine is not linked to the maintenance system";

  const dialog = (
    <QuickReportDialog
      open={reportOpen}
      assetNodeId={assetNode?.id}
      assetName={assetNode?.name ?? machineName}
      onClose={() => setReportOpen(false)}
    />
  );

  if (compact) {
    return (
      <HideIfNoRole anyOf={CMMS_FIELD_ROLES}>
        <Tooltip title={tooltipText}>
          <span>
            <IconButton
              aria-label="Report problem"
              size="large"
              disabled={!linked}
              onClick={() => setReportOpen(true)}
            >
              <ReportProblemOutlinedIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
        {dialog}
      </HideIfNoRole>
    );
  }

  const button = (
    <span>
      <Button
        variant="contained"
        color="warning"
        startIcon={<ReportProblemIcon />}
        disabled={!linked}
        onClick={() => setReportOpen(true)}
      >
        Report problem
      </Button>
    </span>
  );

  return (
    <HideIfNoRole anyOf={CMMS_FIELD_ROLES}>
      {linked
        ? button
        : <Tooltip title={tooltipText}>{button}</Tooltip>}
      {dialog}
    </HideIfNoRole>
  );
}
